import {gql} from '@apollo/client';
import {
  ComplaintTaskCandidate,
  ComplaintTaskContext,
  ComplaintTaskDetail,
  ComplaintTaskFacet,
  ComplaintTaskFilterInput,
  ComplaintTaskPage,
  ComplaintTaskRow,
  ComplaintTaskStage,
} from './types';

const ROW_FIELDS = `
    taskId
    taskNo
    subType
    subTypeName
    priority
    emotionLevel
    customerId
    customerName
    customerLevel
    chatName
    warehouseId
    warehouseName
    ownerHubId
    ownerHubName
    title
    trackingNos
    status
    startAt
    assignedAt
    actualStartAt
    finishAt
    responseDueAt
    resolveDueAt
    responseBreached
    resolveBreached
    assigneeAccountId
    assigneeName
    assigneeRole
    assignMode
`;

export const GET_COMPLAINT_TASK_CONTEXT = gql`
    query ComplaintTaskContext {
        complaintTaskContext {
            accountId
            name
            systemAdmin
            manager
            tier
            hubOptions { id name }
        }
    }
`;

export interface ComplaintTaskContextResponse {
  complaintTaskContext: ComplaintTaskContext;
}

export const GET_COMPLAINT_TASKS = gql`
    query ComplaintTasks($filter: ComplaintTaskFilterInput, $pageNum: Int, $pageSize: Int) {
        complaintTasks(filter: $filter, pageNum: $pageNum, pageSize: $pageSize) {
            list { ${ROW_FIELDS} canAssign }
            pageNum
            pageSize
            total
            serverNow
        }
    }
`;

export interface ComplaintTasksResponse {
  complaintTasks: ComplaintTaskPage;
}

export interface ComplaintTasksVariables {
  filter: ComplaintTaskFilterInput;
  pageNum: number;
  pageSize: number;
}

export const GET_COMPLAINT_TASK_FACETS = gql`
    query ComplaintTaskFacets($filter: ComplaintTaskFilterInput) {
        complaintTaskFacets(filter: $filter) {
            total
            byStatus { key count }
            responseOverdue
            resolveOverdue
            byPriority { key count }
            bySubType { key count }
            byHub { key count }
        }
    }
`;

export interface ComplaintTaskFacetsResponse {
  complaintTaskFacets: ComplaintTaskFacet;
}

export const GET_COMPLAINT_TASK_DETAIL = gql`
    query ComplaintTaskDetail($taskId: Long!) {
        complaintTaskDetail(taskId: $taskId) {
            task { ${ROW_FIELDS} }
            messages { speaker content sentAt isCustomer }
            logs { action fromValue toValue reason operatorType operatorName occurredAt extraJson }
            resolutionContent
            resolvedBy
            resolvedAt
            attachments { id fileName contentType url }
            serverNow
            canAssign
            canProcess
        }
    }
`;

export interface ComplaintTaskDetailResponse {
  complaintTaskDetail: ComplaintTaskDetail;
}

export const GET_COMPLAINT_TASK_ASSIGN_CANDIDATES = gql`
    query ComplaintTaskAssignCandidates($taskId: Long!) {
        complaintTaskAssignCandidates(taskId: $taskId) { accountId name roleCode hubId self }
    }
`;

export interface ComplaintTaskAssignCandidatesResponse {
  complaintTaskAssignCandidates: ComplaintTaskCandidate[];
}

export const GET_MY_COMPLAINT_TASKS = gql`
    query MyComplaintTasks($stage: String!, $viewAsAccountId: Long) {
        myComplaintTasks(stage: $stage, viewAsAccountId: $viewAsAccountId) {
            list { ${ROW_FIELDS} }
            serverNow
        }
    }
`;

export interface MyComplaintTasksResponse {
  myComplaintTasks: {list: ComplaintTaskRow[]; serverNow?: string | null};
}

export interface MyComplaintTasksVariables {
  stage: ComplaintTaskStage;
  viewAsAccountId?: number;
}

/** 手机版队列：按登录人可见范围（同管理端），无查看视角（10-14 确认）。 */
export const GET_MOBILE_COMPLAINT_TASKS = gql`
    query MobileComplaintTasks($stage: String!) {
        mobileComplaintTasks(stage: $stage) {
            list { ${ROW_FIELDS} }
            serverNow
        }
    }
`;

export interface MobileComplaintTasksResponse {
  mobileComplaintTasks: {list: ComplaintTaskRow[]; serverNow?: string | null};
}

/**
 * PC 承接人列表（管理端承接人筛选 / 作业端查看视角）：按登录人层级返回——
 * 系统管理员全部；大区负责人、客服所在 HUB；+1 主管自己及直属下级；其余仅自己。本人在首位。
 */
export const GET_COMPLAINT_TASK_ASSIGNEES = gql`
    query ComplaintTaskAssignees {
        complaintTaskAssignees { accountId name roleCode hubId self }
    }
`;

export interface ComplaintTaskAssigneesResponse {
  complaintTaskAssignees: ComplaintTaskCandidate[];
}
